import { BaseControl } from "../controls";

/**
 * @interface WidgetInterface
 */
export class WidgetInterface {

    /**
     * The name of the widget
     * @return {string}
     */
    getName() {
        throw new Error(`${this.constructor.name}: please add getName() in your widget subclass.`);
    }

    /**
     * The category of the widget, used for grouping in the widget browser
     * @return {string}
     */
    getCategory() {
        return 'default';
    }

    /**
     * The tenant module flag this widget requires to be visible in the widget
     * browser. Returns null when the widget is always available.
     *
     * Supported values:
     *   - 'housevisits' — requires useSettings().housevisits === true
     *   - 'personas'    — requires useSettings().personasOn === 'flex'
     *   - null          — always visible (default)
     *
     * @return {'housevisits' | 'personas' | null}
     */
    getRequiredModule() {
        return null;
    }

    /**
     * Whether this widget counts over all participants (Personas) of the
     * tenant. Such a widget says nothing in a report about one participant:
     * the widget browser hides it and a placed one shows a notice instead
     * (PersonaScopeGuard). See docs/features/livereport-per-deelnemer.md (T5).
     *
     * @return {boolean}
     */
    countsAllPersonas() {
        return false;
    }

    /**
     * The icon representing the widget
     * @return {React.ReactNode}
     */
    getIcon() {
        throw new Error(`${this.constructor.name}: please add getIcon() in your widget subclass.`);
    }
    
    /**
     * The controls for the widget
     * @return {Record<string, BaseControl>}
     */
    getControls() {
        throw new Error(`${this.constructor.name}: please add getControls() in your widget subclass.`);
    }

    /**
     * Rendering logic for the widget 
     * @abstract 
     * @param {React.PropsWithChildren<any>} props
     * @return {React.ReactNode}
     * 
     * @todo infer props from getControls, should work with a generic type, but JSdoc does not pick it up...
     * ```
     * __@__param {{
     *     [K in keyof ReturnType<typeof this.getControls>]: ReturnType<typeof this.getControls>[K]['defaultValue']
     * }} props
     * ```
     */
    render(props) {
        throw new Error(`${this.constructor.name}: please add render() in your widget subclass.`);
    }
}
